/**
 * Phase 4 — Audit Log
 * Admins see full log; tutors see their own actions.
 */
import { useState, useEffect } from 'react';
import { apiClient } from '@/lib/api';
import { AuditLogEntry } from '@/lib/types';

const ACTION_BADGE: Record<string, string> = {
  create: 'bg-emerald-100 text-emerald-700',
  update: 'bg-blue-100 text-blue-700',
  delete: 'bg-red-100 text-red-700',
  publish: 'bg-teal-100 text-teal-700',
  unpublish: 'bg-amber-100 text-amber-700',
  archive: 'bg-slate-100 text-slate-600',
  reorder: 'bg-purple-100 text-purple-700',
};

function formatDate(ts: string) {
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return ts;
  }
}

export default function AuditLogPage() {
  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    const res = await apiClient.getAuditLog();
    if (res.success && res.data) {
      const items = Array.isArray(res.data) ? res.data : (res.data as any)?.results || [];
      setEntries(items);
    } else {
      setError(res.error || 'Failed to load audit log');
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="mb-6">
        <p className="text-xs font-medium uppercase tracking-wide text-amber-700">Phase 4</p>
        <h1 className="mt-0.5 text-xl font-bold text-slate-900">Audit Log</h1>
        <p className="text-sm text-slate-500 mt-1">Complete record of authoring activity on this platform.</p>
      </div>

      {error && (
        <div className="mb-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      ) : entries.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-200 py-20 text-center">
          <p className="text-slate-400 text-sm">No audit events recorded yet.</p>
        </div>
      ) : (
        <div className="rounded-xl border border-slate-100 bg-white shadow-sm overflow-hidden">
          <table className="min-w-full divide-y divide-slate-100">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">When</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Actor</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Action</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Entity</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Title</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {entries.map(entry => (
                <tr key={entry.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-3 text-xs text-slate-400 whitespace-nowrap">{formatDate(entry.timestamp)}</td>
                  <td className="px-4 py-3 text-sm text-slate-700">{entry.actor_name || '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${ACTION_BADGE[entry.action] || 'bg-slate-100 text-slate-600'}`}>
                      {entry.action}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {entry.entity_type}#{entry.entity_id}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-700 max-w-xs truncate">
                    {entry.entity_title || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
